import React from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import styled from 'styled-components';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import Tooltip from 'components/controls/Tooltip';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import AuthService from 'services/AuthService';
import { useEPDCommentLog } from 'services/api/queries';
import { EPDCommentLogItemModel, EPDCommentLogModel } from 'types/epd-definition/EPDCommentLogModel';
import EpdRoutes from 'routes/EpdRoutes';
import { Container } from 'styles/Styles.styled';
import { H1NewUI, FormPanel } from 'styles/v2/Styles.styled';
import { COMMENT_TYPE_LABELS, HIGHLIGHTED_COMMENT_TYPES } from './commentTypeLabels';

interface RouteParams {
  epdId: string;
}

const versionNumberBody = (row: EPDCommentLogItemModel) =>
  row.versionNumber != null ? String(row.versionNumber).padStart(3, '0') : '-';

const commentTypeBody = (row: EPDCommentLogItemModel) => {
  const label = COMMENT_TYPE_LABELS[row.commentType] ?? row.commentType;
  return (
    <TruncatedCell $highlighted={HIGHLIGHTED_COMMENT_TYPES.has(row.commentType)}>
      {label}
      <Tooltip position="titleEllipsed">{label}</Tooltip>
    </TruncatedCell>
  );
};

const commentedByBody = (row: EPDCommentLogItemModel) => (
  <TruncatedCell>
    {row.commentedBy}
    <Tooltip position="titleEllipsed">{row.commentedBy}</Tooltip>
  </TruncatedCell>
);

const EpdCommentLogPage: React.FunctionComponent = () => {
  const { epdId } = useParams<RouteParams>();
  const isAdmin = !!AuthService.isAdmin();
  const { data, isLoading } = useEPDCommentLog(epdId, isAdmin);

  if (!isAdmin) {
    return <Redirect to={EpdRoutes.forbidden} />;
  }

  return <CommentLogView data={data} isLoading={isLoading} />;
};

export const CommentLogView: React.FC<{ data?: EPDCommentLogModel; isLoading: boolean }> = ({ data, isLoading }) => {
  return (
    <Container style={{ paddingTop: 70, marginTop: 80 }}>
      <H1NewUI>{data ? `Comment log for ${data.registrationNumberDisplay}` : 'Comment log'}</H1NewUI>
      <GridPanel>
        {isLoading ? (
          <Skeleton width="100%" height="12rem" />
        ) : (
          <StyledCommentLogTable
            value={data?.items ?? []}
            emptyMessage="No comments found"
            tableStyle={{ tableLayout: 'fixed' }}
          >
            <Column field="versionNumber" header="Version number" body={versionNumberBody} style={{ width: '124px' }} />
            <Column field="date" header="Date (UTC)" style={{ width: '101px' }} />
            <Column field="commentType" header="Comment type" body={commentTypeBody} style={{ width: '148px' }} />
            <Column field="commentedBy" header="Commented by" body={commentedByBody} style={{ width: '144px' }} />
            <Column field="comment" header="Comment" style={{ width: '521px' }} />
          </StyledCommentLogTable>
        )}
      </GridPanel>
    </Container>
  );
};

const GridPanel = styled(FormPanel)`
  display: block;
  padding: 30px;
  overflow: hidden;
  width: 100%;
  margin-top: 41px;
`;

const StyledCommentLogTable = styled(DataTableStyled)`
  border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};

  .p-datatable-thead > tr > th,
  .p-datatable-tbody > tr > td {
    border-right: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  }
  .p-datatable-thead > tr > th:last-child,
  .p-datatable-tbody > tr > td:last-child {
    border-right: none;
  }
  .p-datatable-thead > tr > th {
    border-top: none;
    border-bottom: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  }
`;

const TruncatedCell = styled.span<{ $highlighted?: boolean }>`
  position: relative;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(props) => (props.$highlighted ? props.theme.colors.statusLabelRedText : 'inherit')};
`;

export default withGreyBackground(EpdCommentLogPage);
